import { useParams } from "react-router-dom";
import {
  useEffect,
  useState,
  useCallback,
  useRef,
  useMemo,
  useContext,
} from "react";
import CarListingLayout from "@/components/car-listing/CarListingLayout";
import CarBasicInfo from "@/components/car-listing/CarBasicInfo";
import BiddingInfo from "@/components/car-listing/bidding/BiddingInfo";
import { BuyNowConfirmDialog } from "@/components/car-listing/bidding/BuyNowConfirmDialog";
import TestProtocol from "@/components/car-listing/TestProtocol";
import ServiceHistory from "@/components/car-listing/ServiceHistory";
import Damages from "@/components/car-listing/Damages";
import CarImageGallery from "@/components/car-listing/CarImageGallery";
import { ListingPriceIntervalsSection } from "@/components/car-listing/ListingPriceIntervalsSection";
import { EditListingPriceIntervalsDialog } from "@/components/car-listing/EditListingPriceIntervalsDialog";
import { mapRatingIntervalsToCategories } from "@/admin/utils/ratingIntervals";
import { ListingDetails, ListingStatus } from "@/types/listing";
import { Separator } from "@/components/ui/separator";
import OtherListingsCarousel from "@/components/car-listing/OtherListingsCarousel";
import { toast } from "sonner";
import { hapticFavoriteToggle } from "@/lib/native/hapticFeedback";
import {
  useBidSocket,
  BidUpdateEvent,
  AutoBidUpdateEvent,
} from "@/hooks/useBidSocket";
import {
  isTimerZero,
  useAuctionTimer,
} from "@/components/car-listing/bidding/TimeRemaining";
import { useListing } from "@/hooks/useListing";
import { useQueryClient } from "@tanstack/react-query";
import TiresSection from "@/components/car-listing/TiresSection";
import { useIdVerification } from "@/hooks/useIdVerification";
import KycRequiredModal from "@/components/kyc/KycRequiredModal";
import { authLabels, auctionsLabels, carDetailsLabels } from "@/i18n";
import { useLanguage } from "@/context/LanguageContext";
import AdditionalComments from "@/components/car-listing/AdditionalComments";
import { HowBuyingWorks } from "@/components/car-listing/HowBuyingWorks";
import { AuthContext } from "@/context/AuthContext";
import { UserType } from "@/types/user";
import { useAuth } from "@/hooks/useAuth";
import { cn } from "@/lib/utils";
import { useNativePlatform } from "@/hooks/useNativePlatform";
import { useIsMobile } from "@/hooks/use-mobile";
import { listingCardTransitionName } from "@/lib/native/listingNavigation";
import { dismissListingHeroTransition } from "@/lib/native/listingHeroTransition";
import { ListingInquiryCard } from "@/components/car-listing/ListingInquiryCard";
import { GuestLoginPrompt } from "@/components/auth/GuestLoginPrompt";
import { OpenInAppBanner } from "@/components/native/OpenInAppBanner";
import { useCelebration } from "@/context/CelebrationContext";
import {
  ColoredOutlineButton,
  OUTLINE_ICON_BUTTON_ICON_CLASS,
  OUTLINE_TEXT_BUTTON_ICON_CLASS,
} from "@/components/ui/colored-outline-button";
import { Gavel, Pencil, Shield } from "lucide-react";
import { ListingManagementService } from "@/services/listing/listingManagementService";
import { useCurrentAdminPermissions } from "@/hooks/useCurrentAdminPermissions";

interface CarDetailsContentProps {
  /** Always `listing.id` (cuid) — never the URL slug. */
  listingId: string;
  listing: ListingDetails;
  listingStatus: ListingStatus;
  isGuestMode?: boolean;
  userId: string;
  addFavorite: (data: {
    listingId: string;
    userId: string;
  }) => Promise<{ message: string }>;
  removeFavorite: (data: {
    listingId: string;
    userId: string;
  }) => Promise<{ message: string }>;
  setListing: React.Dispatch<React.SetStateAction<ListingDetails | null>>;
  /** Overlay sheet/side panel (För dig) — single column, bidding at bottom. */
  presentation?: "page" | "overlay";
}

export const CarDetailsContent = ({
  listingId,
  listing,
  userId,
  listingStatus,
  isGuestMode = false,
  addFavorite,
  removeFavorite,
  setListing,
  presentation = "page",
}: CarDetailsContentProps) => {
  const isOverlay = presentation === "overlay";
  const { campaignId: routeCampaignId, listingId: routeListingKey } =
    useParams<{
      campaignId?: string;
      listingId?: string;
    }>();
  const campaignId = routeCampaignId ?? listing.campaignId ?? "";
  const [guestLoginOpen, setGuestLoginOpen] = useState(false);
  const promptGuestLogin = useCallback(() => setGuestLoginOpen(true), []);
  const columnRef = useRef<HTMLDivElement>(null);
  const biddingInfoRef = useRef<HTMLDivElement>(null);
  const separatorRef = useRef<HTMLDivElement>(null);
  const [biddingStyle, setBiddingStyle] = useState<{
    top: number;
    left: number;
  } | null>(null);
  const rafRef = useRef<number | null>(null);

  const { user } = useAuth();
  const isAdmin = user?.type === UserType.Admin;
  const { canViewBidsOverview, canViewDashboardOverview } =
    useCurrentAdminPermissions();
  /** Listing admin actions (price intervals, bid history) require Hantera annonser. */
  const canManageListingControls = isAdmin && canViewBidsOverview;
  /** Named bid history / detailed stats require Dashboard: detaljerad statistik. */
  const canViewDetailedListingStats = isAdmin && canViewDashboardOverview;
  const { isNative, isNativeTablet } = useNativePlatform();
  const isMobile = useIsMobile();
  const queryClient = useQueryClient();
  const [isTogglingRatingIntervals, setIsTogglingRatingIntervals] =
    useState(false);
  const [isTogglingShowBidHistory, setIsTogglingShowBidHistory] =
    useState(false);
  const [editIntervalsOpen, setEditIntervalsOpen] = useState(false);

  // listingId prop is cuid; route may still be a public slug used as RQ cache key.
  const listingCuid = listing.id || listingId;
  const listingCacheKeys = useMemo(() => {
    const keys = new Set<string>();
    if (listingCuid) keys.add(listingCuid);
    if (routeListingKey) keys.add(routeListingKey);
    return [...keys];
  }, [listingCuid, routeListingKey]);

  const invalidateListingCaches = useCallback(async () => {
    await Promise.all(
      listingCacheKeys.flatMap((key) => [
        queryClient.invalidateQueries({ queryKey: ["listing", key] }),
        queryClient.invalidateQueries({
          queryKey: ["listing", "public", key],
        }),
      ]),
    );
  }, [listingCacheKeys, queryClient]);

  const handleToggleHideRatingIntervals = useCallback(async () => {
    if (!listingCuid) return;
    setIsTogglingRatingIntervals(true);
    try {
      const next = !listing.hideRatingIntervals;
      await ListingManagementService.updateListing({
        id: listingCuid,
        hideRatingIntervals: next,
      });
      await invalidateListingCaches();
      toast.success(
        next ? "Prisintervall dolt på annonsen" : "Prisintervall visas igen",
      );
    } catch (err: any) {
      toast.error(err?.message || "Kunde inte uppdatera prisintervall");
    } finally {
      setIsTogglingRatingIntervals(false);
    }
  }, [
    listing.hideRatingIntervals,
    listingCuid,
    invalidateListingCaches,
  ]);

  const handleToggleShowBidHistory = useCallback(async () => {
    if (!listingCuid) return;
    setIsTogglingShowBidHistory(true);
    try {
      const next = !listing.showBidHistory;
      await ListingManagementService.updateListing({
        id: listingCuid,
        showBidHistory: next,
      });
      await invalidateListingCaches();
      toast.success(
        next
          ? "Budhistorik visas för köpare"
          : "Budhistorik dold för köpare",
      );
    } catch (err: any) {
      toast.error(err?.message || "Kunde inte uppdatera budhistorik");
    } finally {
      setIsTogglingShowBidHistory(false);
    }
  }, [listing.showBidHistory, listingCuid, invalidateListingCaches]);

  const refreshListingIntervals = useCallback(async () => {
    if (listingCacheKeys.length === 0) return;
    await Promise.all([
      ...listingCacheKeys.map((key) =>
        queryClient.invalidateQueries({ queryKey: ["listing", key] }),
      ),
      ...listingCacheKeys.map((key) =>
        queryClient.invalidateQueries({
          queryKey: ["listing", key, "rating-intervals", "fetch"],
        }),
      ),
    ]);
  }, [listingCacheKeys, queryClient]);

  useEffect(() => {
    if (!isNative) return;
    const frame = requestAnimationFrame(() => {
      dismissListingHeroTransition();
    });
    return () => cancelAnimationFrame(frame);
  }, [isNative, listingId]);

  const { data: ratingIntervalsRaw } = useListing().getListingRatingIntervals(
    listingId,
    {
      enabled: !!listingId && !isGuestMode,
    },
  );
  const priceCategories = useMemo(
    () => mapRatingIntervalsToCategories(ratingIntervalsRaw),
    [ratingIntervalsRaw],
  );

  // Direct purchase: hide market intervals when asking price is above "Rimligt".
  // Admin can also force-hide via listing.hideRatingIntervals.
  const showPriceIntervals = useMemo(() => {
    if (listing.hideRatingIntervals) return false;
    if (priceCategories.length === 0) return false;
    if (listing.listingType !== "direct-purchase") return true;
    const price =
      listing.directPurchaseDetails?.currentPrice ??
      listing.directPurchaseDetails?.originalPrice;
    if (price == null) return true;
    const fair =
      priceCategories.find((c) => c.dots === 3) ??
      priceCategories.find((c) => /rimligt|fair/i.test(c.label ?? ""));
    if (!fair) return true;
    return price <= fair.upperBound;
  }, [
    listing.hideRatingIntervals,
    priceCategories,
    listing.listingType,
    listing.directPurchaseDetails?.currentPrice,
    listing.directPurchaseDetails?.originalPrice,
  ]);

  const authContext = useContext(AuthContext);
  const companyId = authContext?.companyId ?? null;
  const viewerScopeKey = useMemo(() => {
    if (companyId) return `c:${companyId}`;
    if (listing.automaticBid?.actorId)
      return `a:${listing.automaticBid.actorId}`;
    return null;
  }, [companyId, listing.automaticBid?.actorId]);
  const {
    highestBid,
    effectiveEndTime,
    currentBuyNowPrice: socketCurrentBuyNowPrice,
    canBuyNow: socketCanBuyNow,
    placeBid,
    onBidUpdate,
    onAutoBidUpdate,
  } = useBidSocket(
    isGuestMode ? undefined : listingCuid,
    isGuestMode ? undefined : listing.auctionId,
    listing.auctionDetails?.currentBid,
    listing.actualEndTime ?? listing.endTime,
    { enabled: !isGuestMode && listingStatus === "active" },
  );
  const displayHighestBid = isGuestMode
    ? Number(
        listing.auctionDetails?.currentBid ??
          listing.auctionDetails?.startingPrice ??
          0,
      )
    : Number(highestBid);
  const auctionEndTime =
    effectiveEndTime ?? listing.actualEndTime ?? listing.endTime;

  const auctionTimeRemaining = useAuctionTimer(auctionEndTime ?? "");
  const isAwaitingFinalize =
    listing.listingType === "auction" &&
    listing.status === "active" &&
    Boolean(auctionEndTime) &&
    !!auctionTimeRemaining &&
    isTimerZero(auctionTimeRemaining);

  // Poll parent React Query keys (URL slug and/or cuid) so finalize state updates.
  useEffect(() => {
    if (!isAwaitingFinalize) return;
    const refetch = () => {
      for (const key of listingCacheKeys) {
        void queryClient.refetchQueries({
          queryKey: ["listing", key, userId],
        });
        void queryClient.refetchQueries({
          queryKey: ["listing", "public", key],
        });
      }
    };
    refetch();
    const id = window.setInterval(refetch, 3000);
    return () => window.clearInterval(id);
  }, [isAwaitingFinalize, listingCacheKeys, userId, queryClient]);

  // Replace the existing useEffect with one that updates currentBuyNowPrice
  useEffect(() => {
    if (socketCurrentBuyNowPrice == null && socketCanBuyNow == null) {
      return;
    }
    setListing((prev) =>
      prev
        ? {
            ...prev,
            auctionDetails: prev.auctionDetails
              ? {
                  ...prev.auctionDetails,
                  ...(socketCurrentBuyNowPrice != null
                    ? { currentBuyNowPrice: socketCurrentBuyNowPrice }
                    : {}),
                  ...(socketCanBuyNow != null
                    ? {
                        canBuyNow: socketCanBuyNow,
                      }
                    : {}),
                }
              : prev.auctionDetails,
          }
        : null,
    );
  }, [socketCurrentBuyNowPrice, socketCanBuyNow, setListing]);

  useEffect(() => {
    if (!effectiveEndTime) {
      return;
    }

    setListing((prev) =>
      prev == null || prev.actualEndTime === effectiveEndTime
        ? prev
        : {
            ...prev,
            actualEndTime: effectiveEndTime,
          },
    );
  }, [effectiveEndTime, setListing]);

  // Subscribe to bidUpdate events
  const handleBidUpdate = useCallback(
    (evt: BidUpdateEvent) => {
      const bidAmount =
        typeof evt.bidAmount === "number"
          ? evt.bidAmount
          : parseFloat(String(evt.bidAmount));

      const isMine =
        (viewerScopeKey != null &&
          evt.scopeKey != null &&
          evt.scopeKey === viewerScopeKey) ||
        (viewerScopeKey == null &&
          companyId != null &&
          evt.companyId === companyId);

      if (isMine) {
        setListing((prev) => {
          if (!prev) return null;
          return {
            ...prev,
            myHighestBid: bidAmount,
            bidding: prev.bidding
              ? {
                  ...prev.bidding,
                  currentUserHighestBid: bidAmount,
                  isCurrentUserHighestBidder: true,
                }
              : undefined,
          };
        });
        return;
      }

      setListing((prev) => {
        if (!prev) return null;
        return {
          ...prev,
          bidding: prev.bidding
            ? {
                ...prev.bidding,
                currentUserHighestBid: Number(
                  prev.bidding.currentUserHighestBid,
                ),
                isCurrentUserHighestBidder: false,
              }
            : undefined,
        };
      });
    },
    [companyId, setListing, viewerScopeKey],
  );

  useEffect(() => {
    if (!onBidUpdate) return;

    onBidUpdate(handleBidUpdate);
  }, [onBidUpdate, handleBidUpdate]);

  // KYC / i18n — must be declared before handlers that close over `language`
  const { needsVerification } = useIdVerification();
  const { language } = useLanguage();
  const labels = useMemo(() => authLabels(language), [language]);
  const favLabels = auctionsLabels(language).favorites;
  const buyNowLabels = carDetailsLabels(language).buyNow;

  const handleAutoBidUpdate = useCallback(
    (evt: AutoBidUpdateEvent) => {
      // Only apply config updates for our own scope (company or this actor's auto-bid).
      // Without this, another user's "activated" event overwrites our ceiling in the UI.
      const expectedScope = companyId
        ? `c:${companyId}`
        : listing.automaticBid?.actorId
          ? `a:${listing.automaticBid.actorId}`
          : null;
      if (!expectedScope || evt.scopeKey !== expectedScope) {
        return;
      }

      if (evt.reason === "limit_reached") {
        toast.info(carDetailsLabels(language).biddingInfo.autoBidLimitReached);
      }

      setListing((prev) => {
        if (!prev) return null;
        if (!evt.isActive) {
          return { ...prev, automaticBid: null };
        }
        return {
          ...prev,
          automaticBid: {
            id: prev.automaticBid?.id ?? "live",
            maxAmount: evt.maxAmount ?? prev.automaticBid?.maxAmount ?? 0,
            isActive: true,
            companyId: evt.companyId,
            actorId: prev.automaticBid?.actorId ?? "",
            limitReachedAt: null,
          },
        };
      });
    },
    [companyId, language, listing.automaticBid?.actorId, setListing],
  );

  useEffect(() => {
    if (!onAutoBidUpdate) return;
    onAutoBidUpdate(handleAutoBidUpdate);
  }, [onAutoBidUpdate, handleAutoBidUpdate]);

  // Track if a bid is currently being placed to prevent concurrent bids
  const [isPlacingBid, setIsPlacingBid] = useState(false);

  // Buy Now confirmation dialog state
  const [showBuyNowDialog, setShowBuyNowDialog] = useState(false);
  const [showKycModal, setShowKycModal] = useState(false);

  // Reserve listing / auto-bid hook
  const {
    reserveListingAsync,
    isReservingListing,
    setAutomaticBidAsync,
    cancelAutomaticBidAsync,
    isAutomaticBidMutating,
  } = useListing();

  // Handle buy now button click - opens confirmation dialog or KYC modal
  const handleBuyNow = useCallback(() => {
    if (isGuestMode) {
      promptGuestLogin();
      return;
    }
    // Check KYC status first
    if (needsVerification) {
      setShowKycModal(true);
      return;
    }

    setShowBuyNowDialog(true);
  }, [needsVerification, isGuestMode, promptGuestLogin]);

  const { celebrate } = useCelebration();

  // Handle confirm buy now - calls reserve listing API
  const handleConfirmBuyNow = useCallback(async () => {
    try {
      await reserveListingAsync({ listingId: listingCuid });
      setShowBuyNowDialog(false);
      toast.success(buyNowLabels.reservedSuccess, {
        description: buyNowLabels.reservedDesc,
      });
      const vehicleLabel = [
        listing?.title,
        listing?.vehicleDetails?.regNumber
          ? `(${listing.vehicleDetails.regNumber})`
          : null,
      ]
        .filter(Boolean)
        .join(" ");
      celebrate({
        kind: "purchase",
        title: buyNowLabels.reservedSuccess,
        listingTitle: vehicleLabel || listing?.title,
        subtitle: buyNowLabels.reservedDesc,
      });
      // Update the listing status in local state
      setListing((prev) => (prev ? { ...prev, status: "reserved" } : null));
    } catch (error: any) {
      // Check if error is KYC-related (403 with KYC message)
      const errorMessage = error?.response?.data?.error || error?.message || "";
      if (error?.response?.status === 403 && errorMessage.includes("KYC")) {
        // Show KYC modal instead of toast
        setShowBuyNowDialog(false);
        setShowKycModal(true);
      } else {
        toast.error(buyNowLabels.reserveError);
      }
    }
  }, [
    listingCuid,
    listing?.title,
    listing?.vehicleDetails?.regNumber,
    reserveListingAsync,
    setListing,
    buyNowLabels,
    celebrate,
  ]);

  const handlePlaceBid = async (bidAmount: number) => {
    if (isGuestMode) {
      promptGuestLogin();
      return;
    }
    // Prevent concurrent bid attempts
    if (isPlacingBid) {
      toast.info(buyNowLabels.bidInProgress);
      return;
    }

    setIsPlacingBid(true);
    try {
      const res = await placeBid(bidAmount);
      if ("error" in res) {
        // Handle specific error codes
        if (res.error.code === "BID_IN_PROGRESS") {
          toast.info(res.error.message || buyNowLabels.bidInProgress);
        } else {
          toast.error(res.error.message || buyNowLabels.bidFailed);
        }
        return;
      }
      const d = res.data;
      const myBid = d.placedBidAmount ?? d.highestBid;
      const leading = d.isLeading !== false;

      // Update local state to reflect bid result (may have been outbid by auto-bids)
      setListing((prev) => {
        if (!prev) return null;
        return {
          ...prev,
          myHighestBid: myBid,
          auctionDetails: prev.auctionDetails
            ? { ...prev.auctionDetails, currentBid: d.highestBid }
            : prev.auctionDetails,
          bidding: prev.bidding
            ? {
                ...prev.bidding,
                currentUserHighestBid: myBid,
                isCurrentUserHighestBidder: leading,
                highestBid: d.highestBid,
              }
            : undefined,
        };
      });

      if (d.outbidByAutoBid || leading === false) {
        toast.warning(
          buyNowLabels.bidPlacedOutbidByAuto
            .replace("{amount}", String(myBid))
            .replace("{current}", String(d.highestBid)),
        );
      } else {
        toast.success(
          buyNowLabels.bidPlaced.replace("{amount}", String(myBid)),
        );
      }
    } catch (e: unknown) {
      toast.error(buyNowLabels.bidFailed);
    } finally {
      setIsPlacingBid(false);
    }
  };

  const biLabels = carDetailsLabels(language).biddingInfo;

  const handleSetAutoBid = useCallback(
    async (maxAmount: number) => {
      if (isGuestMode) {
        promptGuestLogin();
        return;
      }
      try {
        const data = await setAutomaticBidAsync({
          listingId: listingCuid,
          maxAmount,
        });
        setListing((prev) => {
          if (!prev) return null;
          return {
            ...prev,
            automaticBid: data.autoBid,
            ...(data.highestBid != null
              ? {
                  myHighestBid: data.highestBid,
                  auctionDetails: prev.auctionDetails
                    ? { ...prev.auctionDetails, currentBid: data.highestBid }
                    : prev.auctionDetails,
                  bidding: prev.bidding
                    ? {
                        ...prev.bidding,
                        currentUserHighestBid: data.highestBid,
                        isCurrentUserHighestBidder: true,
                        highestBid: data.highestBid,
                      }
                    : undefined,
                }
              : {}),
          };
        });
        toast.success(
          biLabels.autoBidActivated.replace(
            "{amount}",
            maxAmount.toLocaleString("sv-SE"),
          ),
        );
      } catch (e: unknown) {
        const message = e instanceof Error ? e.message : biLabels.autoBidFailed;
        toast.error(message || biLabels.autoBidFailed);
      }
    },
    [
      biLabels.autoBidActivated,
      biLabels.autoBidFailed,
      isGuestMode,
      listingCuid,
      promptGuestLogin,
      setAutomaticBidAsync,
      setListing,
    ],
  );

  const handleCancelAutoBid = useCallback(async () => {
    try {
      await cancelAutomaticBidAsync({ listingId: listingCuid });
      setListing((prev) => (prev ? { ...prev, automaticBid: null } : null));
      toast.success(biLabels.autoBidCancelled);
    } catch {
      toast.error(biLabels.autoBidFailed);
    }
  }, [
    biLabels.autoBidCancelled,
    biLabels.autoBidFailed,
    cancelAutomaticBidAsync,
    listingCuid,
    setListing,
  ]);

  // Scroll to top when component mounts or when id changes
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [listingId]);

  // Fixed positioning for bidding info on desktop – följer vid scroll, minimal lagg
  useEffect(() => {
    if (!listing) return;
    const HEADER_OFFSET = 64; // Header + marginal
    const STICK_TOP = HEADER_OFFSET + 16;

    const updatePosition = () => {
      if (
        !columnRef.current ||
        !biddingInfoRef.current ||
        !separatorRef.current
      )
        return;
      const isDesktop = window.matchMedia("(min-width: 1024px)").matches;
      if (!isDesktop || isNative) {
        setBiddingStyle(null);
        return;
      }

      const colRect = columnRef.current.getBoundingClientRect();
      const cardRect = biddingInfoRef.current.getBoundingClientRect();
      const sepRect = separatorRef.current.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const left = colRect.left;

      // Max top så att kortet inte korsar dividern ovanför "Upptäck våra andra annonser"
      const maxTop = sepRect.top - cardRect.height - 16;

      let top: number;
      if (colRect.top >= STICK_TOP) {
        // Längst upp – naturligt läge (alltid under header)
        top = Math.max(HEADER_OFFSET, colRect.top);
      } else {
        // Scrollat ned – fäst under header (STICK_TOP), aldrig korsa dividern (maxTop)
        top = Math.min(STICK_TOP, maxTop);
      }
      setBiddingStyle({ top, left });
    };

    const handleScroll = () => {
      if (rafRef.current != null) cancelAnimationFrame(rafRef.current);
      rafRef.current = requestAnimationFrame(() => {
        updatePosition();
        rafRef.current = null;
      });
    };

    const resizeObserver = new ResizeObserver(() => {
      handleScroll();
    });
    if (columnRef.current) resizeObserver.observe(columnRef.current);

    updatePosition();
    window.addEventListener("scroll", handleScroll, { passive: true });
    window.addEventListener("resize", handleScroll);

    return () => {
      if (rafRef.current != null) cancelAnimationFrame(rafRef.current);
      resizeObserver.disconnect();
      window.removeEventListener("scroll", handleScroll);
      window.removeEventListener("resize", handleScroll);
    };
  }, [listing, isNative]);

  const handleFavoriteToggle = useCallback(
    async (isFavorite: boolean) => {
      if (!listingCuid) return;
      if (isGuestMode) {
        promptGuestLogin();
        return;
      }
      hapticFavoriteToggle();

      // Optimistically update local state
      setListing((prev) => (prev ? { ...prev, isFavorite } : null));

      if (isFavorite) {
        try {
          await addFavorite({ listingId: listingCuid, userId });
          toast.success(favLabels.addSuccess);
        } catch (error) {
          // Revert optimistic update on error
          setListing((prev) => (prev ? { ...prev, isFavorite: false } : null));
          toast.error(favLabels.addError);
        }
      } else {
        try {
          await removeFavorite({ listingId: listingCuid, userId });
          toast.success(favLabels.removeSuccess);
        } catch (error) {
          // Revert optimistic update on error
          setListing((prev) => (prev ? { ...prev, isFavorite: true } : null));
          toast.error(favLabels.removeError);
        }
      }
    },
    [
      listingCuid,
      userId,
      addFavorite,
      removeFavorite,
      setListing,
      favLabels,
      isGuestMode,
      promptGuestLogin,
    ],
  );

  const guestEquipment =
    (listing.vehicleDetails as { equipmentPreview?: string[] })
      .equipmentPreview ??
    listing.vehicleDetails.equipment ??
    [];
  const guestEquipmentTotal =
    (listing.vehicleDetails as { equipmentTotalCount?: number })
      .equipmentTotalCount ?? guestEquipment.length;
  const guestHasMoreImages = (() => {
    const backendFlag =
      (listing.vehicleTest.images as { hasMoreImages?: boolean })
        .hasMoreImages ?? false;

    const exteriorCount = Array.isArray(listing.vehicleTest.images.exterior)
      ? listing.vehicleTest.images.exterior.filter((img: unknown) => {
          // Exclude 360 frames (those are not shown in CarImageGallery)
          const frameIndex =
            (img as { frameIndex?: unknown; frame_index?: unknown })
              .frameIndex ??
            (img as { frameIndex?: unknown; frame_index?: unknown })
              .frame_index;
          return frameIndex == null;
        }).length
      : 0;
    const interiorCount = Array.isArray(listing.vehicleTest.images.interior)
      ? listing.vehicleTest.images.interior.length
      : 0;

    return backendFlag || exteriorCount + interiorCount > 2;
  })();

  const getSaleType = () => {
    if (!listing) return "auction";
    return listing.listingType === "auction" ? "auction" : "direct";
  };

  const showListingInquiry =
    !isAdmin &&
    !isGuestMode &&
    listing.status !== "completed" &&
    listing.status !== "ended";

  const adminBidHistoryToggle =
    canManageListingControls &&
    listing.listingType === "auction" &&
    listing.status !== "completed" &&
    listing.status !== "ended" ? (
      <div className="mt-3 w-full">
        <ColoredOutlineButton
          type="button"
          variant="blue"
          tintedBackground
          wrapperClassName="w-full"
          innerClassName="w-full min-h-12 h-auto py-3.5 text-sm md:min-h-9 md:h-9 md:py-2"
          disabled={isTogglingShowBidHistory}
          onClick={() => void handleToggleShowBidHistory()}
        >
          <Gavel
            className={OUTLINE_TEXT_BUTTON_ICON_CLASS}
            strokeWidth={2.35}
            aria-hidden
          />
          {listing.showBidHistory
            ? "Dölj budhistorik för köpare"
            : "Visa budhistorik för köpare"}
        </ColoredOutlineButton>
      </div>
    ) : null;

  const listingInquiryFooter = showListingInquiry ? (
    <ListingInquiryCard className="mt-3 w-full" listingId={listingId} />
  ) : null;

  const biddingFooterSlot = adminBidHistoryToggle ?? listingInquiryFooter;

  const biddingInfoNode = (
    <BiddingInfo
      onPlaceBid={handlePlaceBid}
      onBuyNow={handleBuyNow}
      guestLocked={isGuestMode}
      onGuestLoginRequired={promptGuestLogin}
      currentBid={displayHighestBid}
      isPlacingBid={isPlacingBid}
      status={listing.status}
      nextMinBid={
        displayHighestBid
          ? Number(displayHighestBid) +
            Number(listing.auctionDetails?.minimumBidIncrement)
          : Number(listing.auctionDetails?.startingPrice) +
            Number(listing.auctionDetails?.minimumBidIncrement)
      }
      endTime={auctionEndTime}
      saleType={getSaleType()}
      isOwnAuction={listing.isMyListing && !isAdmin}
      isCompleted={
        listing.status === "completed" || listing.status === "ended"
      }
      isAdmin={isAdmin}
      canViewDetailedStats={canViewDetailedListingStats}
      startingPrice={
        listing.listingType === "direct-purchase"
          ? listing.directPurchaseDetails?.originalPrice
          : listing.auctionDetails?.startingPrice
      }
      currentPrice={
        listing.listingType === "direct-purchase"
          ? listing.directPurchaseDetails?.currentPrice
          : listing.auctionDetails?.currentBid ||
            listing.auctionDetails?.startingPrice
      }
      vehicleMake={listing.vehicleDetails.make}
      vehicleModel={listing.vehicleDetails.model}
      vehicleYear={listing.vehicleDetails.year}
      regNumber={listing.vehicleDetails.regNumber}
      listingTitle={listing.title}
      listingType={listing.listingType}
      auctionStartingPrice={listing.auctionDetails?.startingPrice}
      auctionCurrentBid={listing.auctionDetails?.currentBid}
      directPurchasePrice={listing.directPurchaseDetails?.currentPrice}
      vatCar={Boolean(
        listing.vehicleTest?.vatCar ??
          (listing.vehicleTest as { isVatCar?: boolean } | undefined)?.isVatCar,
      )}
      currentBuyNowPrice={listing.auctionDetails?.currentBuyNowPrice}
      canBuyNow={listing.auctionDetails?.canBuyNow}
      isBuyNowTriggered={listing.auctionDetails?.isBuyNowTriggered}
      listingId={listing.id}
      listingSlug={listing.slug}
      views={listing.bidding?.views || listing.views}
      uniqueViews={listing.bidding?.uniqueViews}
      deliveryFee={listing.deliveryFee}
      sellerPickupCity={listing.sellerPickupCity}
      sellerName={listing.sellerDisplayName}
      sellerEmail={listing.sellerEmail}
      myHighestBid={
        listing.bidding?.currentUserHighestBid || listing.myHighestBid
      }
      isLeading={listing.bidding?.isCurrentUserHighestBidder || false}
      requestedPrice={listing.requestedPrice}
      reservationPrice={
        listing.auctionDetails
          ? ((listing.auctionDetails as any).reservationPrice ??
            (listing.auctionDetails as any).reservePrice)
          : undefined
      }
      reservedPrice={listing.auctionDetails?.reservedPrice}
      footerSlot={biddingFooterSlot}
      automaticBid={
        listing.automaticBid?.isActive
          ? {
              id: listing.automaticBid.id,
              maxAmount: listing.automaticBid.maxAmount,
              isActive: true,
            }
          : null
      }
      onSetAutoBid={handleSetAutoBid}
      onCancelAutoBid={handleCancelAutoBid}
      isAutoBidLoading={isAutomaticBidMutating}
      minimumBidIncrement={Number(
        listing.auctionDetails?.minimumBidIncrement ?? 500,
      )}
      forceMobileBar={isOverlay}
      // Always contain in overlay sheets — fixed portal (z-85) sits behind
      // För dig listing sheet (z-211) and disappears on native/mobile.
      containedMobileBar={isOverlay}
      showBidHistory={listing.showBidHistory === true}
      onToggleShowBidHistory={
        canManageListingControls
          ? () => void handleToggleShowBidHistory()
          : undefined
      }
      isTogglingShowBidHistory={isTogglingShowBidHistory}
    />
  );

  const detailsBody = (
    <>
      <div
        className={isOverlay || isNative ? "" : "-mx-4 sm:mx-0"}
        style={
          isNative && !isOverlay
            ? ({
                viewTransitionName: listingCardTransitionName(listing.id),
              } as React.CSSProperties)
            : undefined
        }
      >
        <CarImageGallery
          images={{
            exterior: listing.vehicleTest.images.exterior,
            interior: listing.vehicleTest.images.interior,
          }}
          isFavorite={listing.isFavorite}
          listingId={listing.id}
          onFavoriteChange={handleFavoriteToggle}
          isGuestMode={isGuestMode}
          guestHasMoreImages={guestHasMoreImages}
          onGuestLoginRequired={promptGuestLogin}
          edgeToEdge={isOverlay}
        />
        {showPriceIntervals && (
          <div
            className={
              isNative && !isOverlay
                ? "mt-3 px-[var(--native-page-inset-x)]"
                : "mt-3 px-4 sm:px-0"
            }
          >
            <ListingPriceIntervalsSection priceCategories={priceCategories} />
          </div>
        )}
        {canManageListingControls && priceCategories.length > 0 && (
          <div
            className={cn(
              "mt-2 flex flex-wrap gap-2",
              isNative
                ? "px-[var(--native-page-inset-x)]"
                : "px-4 sm:px-0",
            )}
          >
            {isMobile || isNative ? (
              <>
                <ColoredOutlineButton
                  type="button"
                  variant="blue"
                  tintedBackground
                  size="icon"
                  disabled={isTogglingRatingIntervals}
                  onClick={() => void handleToggleHideRatingIntervals()}
                  aria-label={
                    listing.hideRatingIntervals
                      ? "Visa prisintervall"
                      : "Dölj prisintervall"
                  }
                  title={
                    listing.hideRatingIntervals
                      ? "Visa prisintervall"
                      : "Dölj prisintervall"
                  }
                >
                  <Shield
                    className={OUTLINE_ICON_BUTTON_ICON_CLASS}
                    strokeWidth={2.35}
                  />
                </ColoredOutlineButton>
                <ColoredOutlineButton
                  type="button"
                  variant="blue"
                  tintedBackground
                  size="icon"
                  onClick={() => setEditIntervalsOpen(true)}
                  aria-label="Redigera prisintervall"
                  title="Redigera prisintervall"
                >
                  <Pencil
                    className={OUTLINE_ICON_BUTTON_ICON_CLASS}
                    strokeWidth={2.35}
                  />
                </ColoredOutlineButton>
              </>
            ) : (
              <>
                <ColoredOutlineButton
                  type="button"
                  variant="blue"
                  tintedBackground
                  size="sm"
                  disabled={isTogglingRatingIntervals}
                  onClick={() => void handleToggleHideRatingIntervals()}
                  innerClassName="px-4 py-2 flex-shrink-0 text-sm"
                >
                  <Shield
                    className={OUTLINE_TEXT_BUTTON_ICON_CLASS}
                    strokeWidth={2.35}
                  />
                  <span>
                    {listing.hideRatingIntervals
                      ? "Visa prisintervall"
                      : "Dölj prisintervall"}
                  </span>
                </ColoredOutlineButton>
                <ColoredOutlineButton
                  type="button"
                  variant="blue"
                  tintedBackground
                  size="sm"
                  onClick={() => setEditIntervalsOpen(true)}
                  innerClassName="px-4 py-2 flex-shrink-0 text-sm"
                >
                  <Pencil
                    className={OUTLINE_TEXT_BUTTON_ICON_CLASS}
                    strokeWidth={2.35}
                  />
                  <span>Redigera intervall</span>
                </ColoredOutlineButton>
              </>
            )}
          </div>
        )}
        {canManageListingControls && listing.id && (
          <EditListingPriceIntervalsDialog
            open={editIntervalsOpen}
            onOpenChange={setEditIntervalsOpen}
            listingId={listing.id}
            priceCategories={priceCategories}
            useManualRatingIntervals={listing.useManualRatingIntervals}
            onSaved={refreshListingIntervals}
          />
        )}
      </div>

      <div
        className={cn(
          "space-y-4",
          isNative && !isOverlay && "native-listing-body",
          isOverlay && "px-4",
        )}
      >
        <CarBasicInfo
          listing={listing}
          equipment={
            isGuestMode
              ? guestEquipment
              : listing.vehicleDetails.equipment || []
          }
          isGuestMode={isGuestMode}
          equipmentTotalCount={guestEquipmentTotal}
          onGuestLoginRequired={promptGuestLogin}
        />

        <TiresSection
          images={isGuestMode ? undefined : listing.vehicleTest.images?.tires}
          testData={{
            ...listing.vehicleTest.vehicleTestData,
            vehicleDetails: listing.vehicleDetails,
          }}
          guestLocked={isGuestMode}
          onGuestLoginRequired={promptGuestLogin}
        />

        <Damages
          images={isGuestMode ? undefined : listing.vehicleTest.images?.damage}
          guestLocked={isGuestMode}
          onGuestLoginRequired={promptGuestLogin}
        />

        <ServiceHistory
          serviceImages={
            isGuestMode
              ? undefined
              : listing.vehicleTest.images?.serviceImages
          }
          serviceDetails={
            isGuestMode
              ? undefined
              : listing.vehicleTest.images?.serviceDetails?.serviceDetails
          }
          guestLocked={isGuestMode}
          onGuestLoginRequired={promptGuestLogin}
        />

        <TestProtocol
          vehicleTest={listing.vehicleTest}
          showComments={false}
          guestMode={isGuestMode}
        />

        <AdditionalComments comments={listing.vehicleTest.comments} />

        <HowBuyingWorks className="mt-2" />
      </div>
    </>
  );

  const dialogs = (
    <>
      <GuestLoginPrompt
        open={guestLoginOpen}
        onOpenChange={setGuestLoginOpen}
      />
      <BuyNowConfirmDialog
        open={showBuyNowDialog}
        onOpenChange={setShowBuyNowDialog}
        onConfirm={handleConfirmBuyNow}
        isLoading={isReservingListing}
      />
      <KycRequiredModal
        isOpen={showKycModal}
        onClose={() => setShowKycModal(false)}
        actionName={labels.kyc?.required?.actions?.buy}
      />
    </>
  );

  if (isOverlay) {
    return (
      <div
        className={cn(
          "relative flex h-full min-h-0 w-full flex-1 flex-col overflow-hidden bg-background text-foreground",
        )}
      >
        <div className="min-h-0 flex-1 space-y-4 overflow-y-auto overscroll-contain pb-4">
          {detailsBody}
        </div>
        {/* Flush to sheet bottom — bar bg + safe-area pad, no gap below */}
        <div className="relative z-[20] mt-auto w-full shrink-0 bg-secondary pb-[env(safe-area-inset-bottom,0px)]">
          {biddingInfoNode}
        </div>
        {dialogs}
      </div>
    );
  }

  return (
    <CarListingLayout type={getSaleType()}>
      {campaignId ? (
        <OpenInAppBanner
          campaignId={campaignId}
          listingId={listing.slug || listingId}
          stackAboveBiddingBar={!isNative && isMobile}
        />
      ) : null}
      <div
        className={cn(
          "grid gap-6 pb-24 lg:pb-0",
          isNativeTablet ? "grid-cols-1" : "grid-cols-1 lg:grid-cols-3",
        )}
      >
        <div className="lg:col-span-2 space-y-4">{detailsBody}</div>

        <div
          ref={columnRef}
          className={
            isNative
              ? "native-listing-bidding-slot lg:col-span-1"
              : "lg:col-span-1"
          }
        >
          <div
            ref={biddingInfoRef}
            className="w-full lg:w-80 z-[20]"
            style={
              biddingStyle != null
                ? {
                    position: "fixed",
                    top: `${biddingStyle.top}px`,
                    left: `${biddingStyle.left}px`,
                    width: "20rem",
                  }
                : undefined
            }
          >
            {biddingInfoNode}
          </div>
        </div>
      </div>
      <div
        ref={separatorRef}
        className={isNative ? "native-listing-body" : undefined}
      >
        <Separator className="my-8 h-0.5" />
      </div>
      <div className={isNative ? "native-listing-carousel-wrap" : undefined}>
        <OtherListingsCarousel currentListingId={listing.id || listingId} />
      </div>
      {dialogs}
    </CarListingLayout>
  );
};
